
import React, { useState, useEffect } from 'react';
import { type CrawlResult, type EnhancedCrawlResult } from '@/types/crawler';
import { useFaqExpert } from '@/hooks/faq-expert';
import { toast } from 'sonner';
import FaqUrlInput from './FaqUrlInput';
import FaqGenerator from './FaqGenerator';
import FaqTabs from './FaqTabs';
import EmptyState from './EmptyState';
import ErrorDisplay from '../errors/ErrorDisplay';
import { Sparkles, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FaqExpertWidgetProps {
  result: CrawlResult | EnhancedCrawlResult | null;
  onRetry?: () => void;
}

const FaqExpertWidget: React.FC<FaqExpertWidgetProps> = ({ 
  result,
  onRetry
}) => {
  const [currentTab, setCurrentTab] = useState<string>("beginner");
  const [showNewAnalysisForm, setShowNewAnalysisForm] = useState<boolean>(false);
  
  const { 
    isLoading, 
    error, 
    faqData, 
    currentUrl,
    generateFaqsForResult, 
    generateFaqsForUrl, 
    reset 
  } = useFaqExpert({
    onSuccess: () => {
      setCurrentTab("beginner");
      setShowNewAnalysisForm(false);
      toast.success('FAQs generated successfully!', {
        description: 'Expert-level FAQs have been created based on your content.'
      });
    },
    onError: (error) => {
      toast.error(`Error generating FAQs: ${error.message}`);
    }
  });

  // Removed auto-generation - FAQs are now manual only

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    
    if (currentUrl) {
      generateFaqsForUrl(currentUrl);
    } else if (result) {
      generateFaqsForResult(result);
    } else {
      toast.error("No content available to retry");
    }
  };

  const handleNewAnalysis = () => {
    setShowNewAnalysisForm(true);
  };

  // Get a colorful background based on the current tab
  const getTabBackground = () => {
    switch (currentTab) {
      case "beginner":
        return "bg-gradient-to-br from-purple-950/20 via-background to-indigo-950/20";
      case "intermediate":
        return "bg-gradient-to-br from-blue-950/20 via-background to-cyan-950/20";
      case "expert":
        return "bg-gradient-to-br from-teal-950/20 via-background to-emerald-950/20";
      case "company":
        return "bg-gradient-to-br from-amber-950/20 via-background to-orange-950/20";
      default:
        return "bg-background";
    }
  };

  return (
    <div className={`space-y-6 ${getTabBackground()} backdrop-blur supports-[backdrop-filter]:bg-background/60 p-6 rounded-lg border border-border/40 transition-all duration-700`}>
      <div className="flex items-center justify-between border-b border-border/40 pb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-foreground">
            <span className="header-gradient fallback-gradient">FAQ Expert</span>
          </h2>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-xs font-medium py-1 px-3 rounded-full text-white shadow-lg shadow-purple-500/20">Beta</div>
      </div>

      <p className="text-muted-foreground">
        Generate expert-level, structured FAQs tailored to your content. These FAQs are designed to enhance your content value by addressing reader questions at different experience levels.
      </p>

      {error && (
        <ErrorDisplay 
          componentName="FAQ Expert" 
          error={error}
          onRetry={handleRetry}
        />
      )}

      {(!faqData || showNewAnalysisForm) && !isLoading && (
        <div className="space-y-4">
          <FaqUrlInput
            onSubmit={generateFaqsForUrl}
            isLoading={isLoading}
          />
          
          {(!result || !result.pages || result.pages.length === 0) && (
            <EmptyState 
              message="No Pages Available" 
              description="Please analyze a website first to have content available for FAQ generation."
            />
          )}
        </div>
      )}
      
      {isLoading && (
        <div className="p-12 flex flex-col items-center justify-center bg-muted/5 rounded-lg border border-border/20">
          <div className="relative h-16 w-16">
            <div className="absolute inset-0 rounded-full animate-ping bg-gradient-to-r from-purple-500 to-indigo-500 opacity-50"></div>
            <div className="relative animate-spin w-16 h-16 border-4 border-t-primary border-primary/20 rounded-full"></div>
          </div>
          <p className="text-center font-medium mt-6">
            Generating FAQ Content
          </p>
          <p className="text-center text-muted-foreground text-sm mt-2">
            This may take up to a minute depending on the content length...
          </p>
        </div>
      )}
      
      {faqData && !isLoading && !showNewAnalysisForm && (
        <>
          <FaqTabs
            faqs={faqData}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            onGenerateNew={handleNewAnalysis}
          />
          
          <div className="mt-6 flex justify-center">
            <Button
              onClick={handleNewAnalysis}
              variant="outline"
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 hover:from-indigo-500/20 hover:to-purple-500/20 transition-all duration-300 border-indigo-500/20 text-center"
            >
              <RotateCw className="h-4 w-4" />
              <span>Analyze Another URL</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default FaqExpertWidget;
